import type { DocumentPageMetadataUserEditSurface } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  CANTILEVER_DOCUMENT_PAGE_TAG,
  DOCUMENT_ID_FIELD,
  getCantileverDocumentEntryId,
  getSystemDocumentBqId,
  PAGE_ID_FIELD,
  PAGE_NUM_FIELD,
  PIPELINE_CONFIG_HASH_FIELD,
  SYSTEM_DOCUMENT_TAG,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { bareDocumentPageId } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import { RolodexEntryType, RolodexFilterOp, RolodexFilterType } from 'types'

type ScopedTags = Record<string, string | string[]>

export type SystemDocumentLoadOptions = {
  type: RolodexEntryType[]
  tags: ScopedTags
  ids?: string[]
  filters?: RolodexFilterType[]
  limit?: number
}

export type SystemDocumentPageLoadOptions = {
  type: RolodexEntryType[]
  tags: ScopedTags
  filters: RolodexFilterType[]
  limit: number
}

type UserEditOrderBy = {
  field: string
  descending: boolean
}

export type MetadataUserEditLoadOptions = {
  type: RolodexEntryType[]
  tags: ScopedTags
  ids?: string[]
  filters?: RolodexFilterType[]
  order_by?: UserEditOrderBy[]
  limit: number
  offset?: number
}

const NEWEST_USER_EDIT_FIRST: UserEditOrderBy[] = [
  { field: 'created_at', descending: true },
]

function documentIdFilter(
  documentIds: string | string[],
  op: RolodexFilterOp = RolodexFilterOp.EQUAL
): RolodexFilterType {
  return {
    field: 'data',
    key: DOCUMENT_ID_FIELD,
    op: Array.isArray(documentIds) ? RolodexFilterOp.IN : op,
    value: documentIds,
  }
}

function pipelineConfigHashFilter(
  pipelineConfigHash: string
): RolodexFilterType {
  return {
    field: 'data',
    key: PIPELINE_CONFIG_HASH_FIELD,
    op: RolodexFilterOp.EQUAL,
    value: pipelineConfigHash,
  }
}

function pageIdFilter(pageIds: string | string[]): RolodexFilterType {
  return {
    field: 'data',
    key: PAGE_ID_FIELD,
    op: Array.isArray(pageIds) ? RolodexFilterOp.IN : RolodexFilterOp.EQUAL,
    value: pageIds,
  }
}

/** Load a system-document by Rolodex entry id when the UUID is already known. */
export function systemDocumentByEntryId(
  entryId: string,
  scopedTags: ScopedTags
): SystemDocumentLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    ids: [entryId],
    tags: scopedTags,
    limit: 1,
  }
}

export function systemDocumentPageByEntryId(
  pageEntryId: string,
  scopedTags: ScopedTags
): SystemDocumentLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    ids: [pageEntryId],
    tags: scopedTags,
    limit: 1,
  }
}

/**
 * Fast path matching pipeline Rolodex loads: filter by data.page_id
 * (`{document_id}_pg_{n}`) under site + pipeline-config tags.
 */
export function systemDocumentPageByPageId(
  bqDocumentId: string,
  pageNum: number,
  scopedTags: ScopedTags
): SystemDocumentPageLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    tags: scopedTags,
    filters: [pageIdFilter(bareDocumentPageId(bqDocumentId, pageNum))],
    limit: 1,
  }
}

/** Batch-load system-document-pages by bare `page_id` values. */
export function systemDocumentPagesByPageIds(
  pageIds: readonly string[],
  scopedTags: ScopedTags
): SystemDocumentPageLoadOptions | undefined {
  if (pageIds.length === 0) return undefined
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    tags: scopedTags,
    filters: [pageIdFilter([...pageIds])],
    limit: pageIds.length,
  }
}

export function systemDocumentPageByPageNum(
  systemDocumentEntryId: string,
  pageNum: number,
  scopedTags: ScopedTags
): SystemDocumentPageLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    tags: {
      ...scopedTags,
      [SYSTEM_DOCUMENT_TAG]: systemDocumentEntryId,
    },
    filters: [
      {
        field: 'data',
        key: PAGE_NUM_FIELD,
        op: RolodexFilterOp.EQUAL,
        value: pageNum,
      },
    ],
    limit: 1,
  }
}

/** Load a document-metadata-user-edit by deterministic UUID5 entry id. */
export function documentMetadataUserEditByEntryId(
  entryId: string,
  siteScopedTags: ScopedTags
): MetadataUserEditLoadOptions {
  return {
    type: [RolodexEntryType.DOCUMENT_METADATA_USER_EDIT],
    ids: [entryId],
    tags: siteScopedTags,
    limit: 1,
  }
}

/** Batch-load document user-edit entries by deterministic UUID5 entry ids. */
export function documentMetadataUserEditsByEntryIds(
  entryIds: string[],
  siteScopedTags: ScopedTags
): MetadataUserEditLoadOptions | undefined {
  if (entryIds.length === 0) return undefined
  return {
    type: [RolodexEntryType.DOCUMENT_METADATA_USER_EDIT],
    ids: entryIds,
    tags: siteScopedTags,
    limit: entryIds.length,
  }
}

/** Load page user-edit events tagged to one cantilever-document-page host. */
export function documentPageMetadataUserEditsByCantileverPageTag(
  cantileverDocumentPageHostId: string,
  siteScopedTags: ScopedTags,
  limit = 20
): MetadataUserEditLoadOptions {
  return {
    type: [RolodexEntryType.DOCUMENT_PAGE_METADATA_USER_EDIT],
    tags: {
      ...siteScopedTags,
      [CANTILEVER_DOCUMENT_PAGE_TAG]: cantileverDocumentPageHostId,
    },
    order_by: NEWEST_USER_EDIT_FIRST,
    limit,
  }
}

type DocumentPageMetadataUserEditsByScopeOptions = {
  limit?: number
  offset?: number
  createdBy?: string
  surface?: DocumentPageMetadataUserEditSurface
}

/** Bulk load of page user-edit entries by tags (no pipeline-config tag). */
export function documentPageMetadataUserEditsByScope(
  scopedTags: ScopedTags,
  options: DocumentPageMetadataUserEditsByScopeOptions = {}
): MetadataUserEditLoadOptions {
  const limit = options.limit ?? 5000
  const createdByEmail = options.createdBy?.trim()
  const filters: RolodexFilterType[] = []
  if (createdByEmail) {
    filters.push({
      field: 'created_by',
      op: RolodexFilterOp.EQUAL,
      value: createdByEmail,
    })
  }
  if (options.surface !== undefined) {
    filters.push({
      field: 'data',
      key: 'surface',
      op: RolodexFilterOp.EQUAL,
      value: options.surface,
    })
  }
  return {
    type: [RolodexEntryType.DOCUMENT_PAGE_METADATA_USER_EDIT],
    tags: scopedTags,
    order_by: NEWEST_USER_EDIT_FIRST,
    limit,
    ...(options.offset === undefined ? {} : { offset: options.offset }),
    ...(filters.length > 0 ? { filters } : {}),
  }
}

/** Site-scoped bulk load of page user-edit entries (no pipeline-config tag). */
export function documentPageMetadataUserEditsBySite(
  siteScopedTags: ScopedTags,
  options: DocumentPageMetadataUserEditsByScopeOptions = {}
): MetadataUserEditLoadOptions {
  return documentPageMetadataUserEditsByScope(siteScopedTags, options)
}

/**
 * Load system-document(s) by stable BigQuery document_id field.
 * Config scope comes from pipeline-config tags; optionally also filter hash.
 */
export function systemDocumentByDocumentId(
  documentId: string,
  scopedTags: ScopedTags,
  pipelineConfigHash?: string
): SystemDocumentLoadOptions {
  const filters: RolodexFilterType[] = [documentIdFilter(documentId)]
  if (pipelineConfigHash) {
    filters.push(pipelineConfigHashFilter(pipelineConfigHash))
  }
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: scopedTags,
    filters,
    limit: 1,
  }
}

/** Batch-load system-documents by BQ document_id values (e.g. search hydration). */
export function systemDocumentsByDocumentIds(
  documentIds: string[],
  scopedTags: ScopedTags,
  limit = 200
): SystemDocumentLoadOptions | undefined {
  if (documentIds.length === 0) {
    return undefined
  }
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: scopedTags,
    filters: [documentIdFilter(documentIds)],
    limit,
  }
}

/** Load system-documents that share a drawing number (doc revision family). */
export function systemDocumentsByDrawingNumber(
  drawingNumber: string,
  scopedTags: ScopedTags,
  limit = 500
): SystemDocumentLoadOptions | undefined {
  const trimmed = drawingNumber.trim()
  if (!trimmed) return undefined
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: scopedTags,
    filters: [
      {
        field: 'data',
        key: 'drawing_number',
        op: RolodexFilterOp.EQUAL,
        value: trimmed,
      },
    ],
    limit,
  }
}

/** Batch-load system-documents by Rolodex entry UUIDs. */
export function systemDocumentsByEntryIds(
  entryIds: string[],
  scopedTags: ScopedTags,
  limit?: number
): SystemDocumentLoadOptions | undefined {
  if (entryIds.length === 0) {
    return undefined
  }
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    ids: entryIds,
    tags: scopedTags,
    limit: limit ?? entryIds.length,
  }
}

const ROLODEX_ENTRY_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

// Legacy ingests mint entry ids as `DOC-<hash>_<confighash>` and leave
// `data.document_id` null, so the suffixed form must route through the
// entry-id path.
const PIPELINE_DOC_ENTRY_ID_RE = /^DOC-[0-9a-f]{32}_[0-9a-f]{12}$/i

/** True for Rolodex entry ids (legacy recent/favorite UUIDs and pipeline
 * `DOC-…_confighash` ids), not bare BQ DOC-… document ids. */
export function isLikelyRolodexEntryId(id: string): boolean {
  return ROLODEX_ENTRY_UUID_RE.test(id) || PIPELINE_DOC_ENTRY_ID_RE.test(id)
}

/**
 * Split mixed navigation ids into BQ document_id values vs Rolodex entry UUIDs
 * so each can use the matching Rolodex load shape.
 */
export function partitionSystemDocumentLookupIds(ids: readonly string[]): {
  documentIds: string[]
  entryIds: string[]
} {
  const documentIds: string[] = []
  const entryIds: string[] = []
  for (const id of ids) {
    if (isLikelyRolodexEntryId(id)) {
      entryIds.push(id)
    } else {
      documentIds.push(id)
    }
  }
  return { documentIds, entryIds }
}

function systemDocumentListIdentity(entry: SystemDocumentEntry): string {
  return getSystemDocumentBqId(entry) ?? entry.entry.id
}

function preferSystemDocument(
  current: SystemDocumentEntry,
  candidate: SystemDocumentEntry
): SystemDocumentEntry {
  const currentHasCantilever =
    getCantileverDocumentEntryId(current) !== undefined
  const candidateHasCantilever =
    getCantileverDocumentEntryId(candidate) !== undefined
  if (candidateHasCantilever && !currentHasCantilever) return candidate
  if (currentHasCantilever && !candidateHasCantilever) return current
  if (candidate.entry.updated_at > current.entry.updated_at) return candidate
  return current
}

/**
 * Collapse multi-config duplicates (site-only loads with no active/preview
 * pipeline-config) to one system-document per stable document identity.
 * Prefers cantilever-tagged hosts, then newest updated_at; preserves first-seen order.
 */
export function dedupeSystemDocumentsByDocumentId(
  entries: SystemDocumentEntry[]
): SystemDocumentEntry[] {
  const preferredByIdentity = new Map<string, SystemDocumentEntry>()
  const order: string[] = []

  for (const entry of entries) {
    const identity = systemDocumentListIdentity(entry)
    const existing = preferredByIdentity.get(identity)
    if (existing === undefined) {
      preferredByIdentity.set(identity, entry)
      order.push(identity)
      continue
    }
    preferredByIdentity.set(identity, preferSystemDocument(existing, entry))
  }

  return order.flatMap((identity) => {
    const entry = preferredByIdentity.get(identity)
    return entry ? [entry] : []
  })
}
